<template>
  <div class="rebuttals-tracking-table">
    <div ref="RebuttalsTable" class="rebuttals-tracking-table__scroll">
      <v-client-table ref="rebuttalsTrackingGrid" class="hiaTable" id="rebuttalsTrackingGrid" :data="tableData" :columns="tableColumns" :options="tableOptions">
        <template slot="checked" slot-scope="props">
          <input type="checkbox" v-model="props.row.checked" :disabled="!isRowSelectable(props.row)" @change="() => onRowCheckboxChange(props.row)" />
        </template>

        <template slot="accountStatus" slot-scope="props">
          <div class="account-status" :title="getAccountStatusTitle(props.row.accountStatus)">
            <i v-if="props.row.accountStatus === 'Active'" class="el-icon-circle-check account-status-icon account-status-icon--active" :title="getAccountStatusTitle('Active')"></i>

            <i v-else-if="props.row.accountStatus === 'No Link'" class="el-icon-warning-outline account-status-icon account-status-icon--no-link" :title="getAccountStatusTitle('No Link')"></i>

            <span :title="getAccountStatusTitle(props.row.accountStatus)">{{ props.row.accountStatus }}</span>
          </div>
        </template>

        <template slot="notificationLastSent" slot-scope="props">
          <div class="notification-last-sent">
            <span class="notification-last-sent__date">{{ formatNotificationLastSent(props.row.notificationLastSent) }}</span>

            <i v-if="props.row.notificationLastSent" class="mdi mdi-history notification-history-icon" title="View notification history" @click="openNotificationHistory(props.row)"></i>
          </div>
        </template>

        <template slot="status" slot-scope="props">
          <span>{{ formatStatusLabel(props.row.status) }}</span>
        </template>

        <template slot="details" slot-scope="props">
          <el-button style="margin-left: 5px" @click.native.prevent="updateAcctList(props.row)" plain round size="mini" icon="mdi mdi-details" title="Records Included" circle> </el-button>
        </template>
      </v-client-table>

      <!-- <table v-if="tableData.length && summaryValues.length" class="hiaTable rebuttals-tracking-table__summary">
        <tbody>
          <tr class="strong emphasis">
            <td v-for="(value, i) in summaryValues" :key="i" :class="i === 0 ? 'textAlignLeft' : ''">{{ value }}</td>
          </tr>
        </tbody>
      </table> -->
    </div>

    <RebuttalNotificationHistoryDialog :visible.sync="historyDialogVisible" :coderName="selectedCoderName" :historyRows="historyRows" :loading="historyLoading" />
  </div>
</template>

<script>
  import axios from '@/utilities/axios-global'
  import { mapState } from 'vuex'
  import dashboardHelper from '@/mixins/dashboardHelper'
  import csvGenerator from '@/mixins/csvGenerator'
  import dateHelper from '@/mixins/date-helper'
  import { isCoderNotifySelectable, isOriginalAuditorNotifySelectable } from './rebuttalTrackingSelect'
  import RebuttalNotificationHistoryDialog from './RebuttalNotificationHistoryDialog'

  const COLUMN_HEADINGS = {
    coder: 'Coder',
    accountStatus: 'Account Status',
    notificationLastSent: 'Notification Last Sent',
    totalRecords: 'Total Records',
    recordsWithFindings: 'Records With Findings',
    recordsWaitingForCoder: 'Waiting For Coder',
    coderDisagreesWithFinding: 'Coder Disagrees',
    recordsWaitingForAuditor: 'Waiting For Auditor',
    recordsCompleted: 'Records Completed',
    status: 'Status',
    noRebuttal: 'No Rebuttal',
    coderAgrees: 'Coder Agrees',
    noOutcomeCompletedRebuttal: 'No Outcome Needed',
    noOutcomeActiveRebuttal: 'Active Rebuttal',
    outcome1: 'Upheld Recommendation',
    outcome2: 'Recommendation Changed - Additional Documentation Provided',
    outcome3: 'Recommendation Changed - Department Policy',
    outcome4: 'Recommendation Changed - Payor Specific Guidelines',
    outcome5: 'Recommendation Changed - Auditor Agrees',
    details: 'Details'
  }

  const LEVEL2_COLUMN_HEADINGS = {
    originalAuditor: 'Original Auditor',
    accountStatus: 'Account Status',
    notificationLastSent: 'Notification Last Sent',
    totalRecords: 'Total Records',
    recordsWithFindings: 'Records With Findings',
    recordsWaitingForCoder: 'Waiting For Original Auditor',
    coderDisagreesWithFinding: 'Original Auditor Disagrees',
    recordsWaitingForAuditor: 'Waiting For Level 2 Auditor',
    recordsCompleted: 'Records Completed',
    status: 'Status',
    details: 'Details'
  }

  export default {
    name: 'RebuttalsTrackingTable',

    mixins: [dashboardHelper, csvGenerator, dateHelper],

    components: {
      RebuttalNotificationHistoryDialog
    },

    props: {
      tableData: {
        type: Array,
        required: true
      },
      hideDetails: {
        type: Boolean,

        default: false
      },
      updateAcctList: {
        type: Function,

        required: true
      },
      rebuttalType: {
        type: String,
        required: true
      },
      projectId: {
        type: Number,
        default: null
      },
      notificationRecordType: {
        type: String,
        default: ''
      }
    },

    data() {
      return {
        summaryData: [],
        historyDialogVisible: false,
        historyLoading: false,
        historyRows: [],
        selectedCoderName: ''
      }
    },

    computed: {
      ...mapState('user/', ['baseApiUrl']),
      columnHeadings() {
        return this.rebuttalType === 'ORIGINAL_AUDITOR' ? LEVEL2_COLUMN_HEADINGS : COLUMN_HEADINGS
      },
      accountStatusRecipientLabel() {
        return this.rebuttalType === 'ORIGINAL_AUDITOR' ? 'original auditor' : 'coder'
      },
      accountStatusColumnTitle() {
        const recipient = this.accountStatusRecipientLabel
        return `Active: an email address exists for the ${recipient}. No Link: no email address exists for the ${recipient}; notifications cannot be sent.`
      },
      tableColumns() {
        const cols = ['checked']

        if (this.rebuttalType === 'CODER') {
          cols.push('coder', 'accountStatus', 'notificationLastSent', 'totalRecords', 'recordsWithFindings', 'recordsWaitingForCoder', 'coderDisagreesWithFinding', 'recordsWaitingForAuditor', 'recordsCompleted', 'status')
        } else if (this.rebuttalType === 'ORIGINAL_AUDITOR') {
          cols.push('originalAuditor', 'accountStatus', 'notificationLastSent', 'totalRecords', 'recordsWithFindings', 'recordsWaitingForCoder', 'coderDisagreesWithFinding', 'recordsWaitingForAuditor', 'recordsCompleted', 'status')
        } else {
          cols.push('totalRecords')
        }

        if (!this.hideDetails) {
          cols.push('details')
        }

        return cols
      },
      tableOptions() {
        const headings = {
          checked: h => {
            return h('input', {
              attrs: {
                type: 'checkbox',
                id: 'rebuttalsTrackingSelectAll'
              },
              on: {
                change: e => this.onSelectAllChange(e)
              }
            })
          }
        }

        this.tableColumns.forEach(col => {
          if (col !== 'checked' && this.columnHeadings[col]) {
            if (col === 'accountStatus') {
              headings[col] = h => {
                return h('span', { attrs: { title: this.accountStatusColumnTitle } }, this.columnHeadings[col])
              }
            } else {
              headings[col] = this.columnHeadings[col]
            }
          }
        })

        const filterable = this.tableColumns.filter(col => col !== 'checked' && col !== 'details')

        return {
          filterByColumn: true,
          filterable,
          sortIcon: { is: 'none' },
          highlightMatches: true,
          perPage: 100,
          orderBy: {
            column: this.rebuttalType === 'CODER' ? 'coder' : this.rebuttalType === 'ORIGINAL_AUDITOR' ? 'originalAuditor' : 'totalRecords',
            ascending: true
          },
          headings,
          texts: {
            filter: '',
            filterBy: '...',
            noResults: 'No Rebuttal Data Available'
          },
          filterAlgorithm: {
            notificationLastSent: (row, query) => {
              const formatted = this.formatNotificationLastSent(row.notificationLastSent)
              return formatted.toLowerCase().includes(query.toLowerCase())
            }
          },
          customSorting: {
            notificationLastSent(ascending) {
              return function(a, b) {
                const dateA = a.notificationLastSent ? new Date(a.notificationLastSent).getTime() : 0
                const dateB = b.notificationLastSent ? new Date(b.notificationLastSent).getTime() : 0

                if (ascending) {
                  return dateA >= dateB ? 1 : -1
                }

                return dateA <= dateB ? 1 : -1
              }
            }
          }
        }
      },
      selectableRows() {
        return this.tableData.filter(row => this.isRowSelectable(row))
      },
      allSelected() {
        const selectable = this.selectableRows

        if (selectable.length === 0) {
          return false
        }

        return selectable.every(r => this.isRowChecked(r))
      },
      summaryValues() {
        const data = this.summaryData.length ? this.summaryData : this.tableData
        const columns = this.tableColumns.map(prop => ({ property: prop }))

        return this.getSummary({ columns, data })
      }
    },
    watch: {
      tableData: {
        handler() {
          this.updateSummaryData()

          this.$nextTick(() => this.syncSelectAllCheckbox())
        },

        deep: true
      }
    },
    mounted() {
      this.updateSummaryData()

      this.syncSelectAllCheckbox()
    },
    updated() {
      this.updateSummaryData()
    },
    methods: {
      updateSummaryData() {
        const grid = this.$refs.rebuttalsTrackingGrid

        this.summaryData = grid?.allFilteredData ?? this.tableData
      },
      downloadTable(filename = 'rebuttals_table.csv') {
        const grid = this.$refs.rebuttalsTrackingGrid
        const rows = grid?.allFilteredData ?? this.tableData

        if (!rows.length) {
          return
        }

        const exportColumns = this.tableColumns.filter(col => col !== 'checked' && col !== 'details')
        const headers = exportColumns.map(col => ({
          key: col,
          value: this.columnHeadings[col] || col
        }))

        const data = rows.map(row => {
          const entry = {}

          exportColumns.forEach(col => {
            if (col === 'notificationLastSent') {
              entry[col] = this.formatNotificationLastSent(row.notificationLastSent)
            } else if (col === 'status') {
              entry[col] = this.formatStatusLabel(row.status)
            } else {
              entry[col] = row[col] ?? ''
            }
          })

          return entry
        })

        const summary = this.getSummary({
          columns: this.tableColumns.map(prop => ({ property: prop })),
          data: this.summaryData.length ? this.summaryData : this.tableData
        })
        const summaryRow = {}

        exportColumns.forEach(col => {
          summaryRow[col] = summary[this.tableColumns.indexOf(col)]
        })

        data.push(summaryRow)

        this.arrayToCsv(data, filename, headers, [])
      },
      isRowChecked(row) {
        return row.checked === true
      },
      onSelectAllChange(e) {
        this.$emit('select-all', e.target.checked)

        this.$nextTick(() => this.syncSelectAllCheckbox())
      },
      onRowCheckboxChange(row) {
        if (!this.isRowSelectable(row)) {
          return
        }

        this.$emit('select-record', { row, checked: this.isRowChecked(row) })

        this.$nextTick(() => this.syncSelectAllCheckbox())
      },
      syncSelectAllCheckbox() {
        const el = this.$el.querySelector('#rebuttalsTrackingSelectAll')

        if (!el) {
          return
        }

        el.indeterminate = false

        el.checked = this.allSelected
      },
      isRowSelectable(row) {
        if (this.rebuttalType === 'ORIGINAL_AUDITOR') {
          return isOriginalAuditorNotifySelectable(row)
        }

        return isCoderNotifySelectable(row)
      },
      formatStatusLabel(status) {
        if (this.rebuttalType !== 'ORIGINAL_AUDITOR' || !status) {
          return status || ''
        }

        const labels = {
          'Waiting on Coder': 'Waiting on Original Auditor',
          'Waiting on Auditor': 'Waiting on Level 2 Auditor',
          'Waiting on Both': 'Waiting on Both'
        }

        return labels[status] || status
      },
      getAccountStatusTitle(status) {
        const recipient = this.accountStatusRecipientLabel

        if (status === 'Active') {
          return `An email address exists for this ${recipient}; notifications can be sent.`
        }

        if (status === 'No Link') {
          return `No email address exists for this ${recipient}; notifications cannot be sent.`
        }

        return ''
      },
      formatNotificationLastSent(value) {
        if (!value) {
          return ''
        }

        return this.formatDateTime(value)
      },
      async openNotificationHistory(row) {
        if (!this.projectId || !this.notificationRecordType) {
          return
        }

        if (this.rebuttalType === 'ORIGINAL_AUDITOR') {
          if (!row.originalAuditorId) {
            return
          }

          this.selectedCoderName = row.originalAuditor || ''
          this.historyRows = []
          this.historyDialogVisible = true
          this.historyLoading = true

          try {
            const response = await axios.post(`${this.baseApiUrl}/reports/dashboard/rebuttalNotificationHistoryByOriginalAuditor`, {
              projectId: this.projectId,
              originalAuditorId: row.originalAuditorId,
              recordType: this.notificationRecordType
            })

            this.historyRows = response.data || []
          } catch (err) {
            this.historyRows = []
            this.$message({
              message: err.response?.data?.message || err.message || 'Error loading notification history',
              type: 'error'
            })
          } finally {
            this.historyLoading = false
          }

          return
        }

        if (!row.coderId) {
          return
        }

        this.selectedCoderName = row.coder || ''

        this.historyRows = []

        this.historyDialogVisible = true

        this.historyLoading = true

        try {
          const response = await axios.post(`${this.baseApiUrl}/reports/dashboard/rebuttalNotificationHistoryByCoder`, {
            projectId: this.projectId,

            coderId: row.coderId,

            recordType: this.notificationRecordType
          })

          this.historyRows = response.data || []
        } catch (err) {
          this.historyRows = []

          this.$message({
            message: err.response?.data?.message || err.message || 'Error loading notification history',

            type: 'error'
          })
        } finally {
          this.historyLoading = false
        }
      }
    }
  }
</script>

<style scoped>
  .rebuttals-tracking-table__scroll {
    max-height: 425px;

    overflow-y: auto;
  }

  .rebuttals-tracking-table__summary {
    width: 100%;

    border-top: 1px solid #ebeef5;
  }

  .rebuttals-tracking-table__summary td {
    padding: 8px 10px;

    text-align: center;

    background-color: #f5f7fa;

    font-weight: bold;
  }

  .account-status {
    display: flex;

    align-items: center;

    gap: 6px;
  }

  .account-status-icon {
    font-size: 18px;
  }

  .account-status-icon--active {
    color: #67c23a;
  }

  .account-status-icon--no-link {
    color: #e6a23c;
  }

  .notification-last-sent {
    display: flex;

    flex-direction: row;

    align-items: center;

    justify-content: space-between;

    gap: 8px;

    width: 100%;
  }

  .notification-last-sent__date {
    min-width: 0;
  }

  .notification-history-icon {
    flex-shrink: 0;

    font-size: 16px;

    color: #909399;

    cursor: pointer;
  }

  .notification-history-icon:hover {
    color: #409eff;
  }
</style>
